import React from 'react';
import { ShippingPrice, DistrictDto } from '../../types';

interface FormData {
  locationType: 'province' | 'district';
  provinceId: string;
  districtId: string;
  price: string;
  notes: string;
  affectedDate: string;
  reason: string;
}

interface PriceFormModalProps {
  showModal: boolean;
  editingPrice: ShippingPrice | null;
  formData: FormData;
  provinces: any[];
  districts: DistrictDto[];
  selectedProvinceId: number | null;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  onFormChange: (updates: Partial<FormData>) => void;
  onProvinceChange: (provinceId: string) => void;
  t: (key: string) => string;
}

const PriceFormModal: React.FC<PriceFormModalProps> = ({
  showModal,
  editingPrice,
  formData,
  provinces,
  districts,
  onSubmit,
  onClose,
  onFormChange,
  onProvinceChange,
  t
}) => {
  if (!showModal) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {editingPrice ? t('edit.shipping.price') : t('add.shipping.price')}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body">
              {!editingPrice && (
                <div className="mb-3">
                  <label className="form-label">{t('location.type')}</label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="locationType"
                        value="province"
                        checked={formData.locationType === 'province'}
                        onChange={(e) => onFormChange({ 
                          locationType: e.target.value as 'province' | 'district',
                          districtId: '',
                          provinceId: ''
                        })}
                      />
                      <label className="form-check-label">{t('province')}</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="locationType"
                        value="district"
                        checked={formData.locationType === 'district'}
                        onChange={(e) => onFormChange({ 
                          locationType: e.target.value as 'province' | 'district',
                          districtId: '',
                          provinceId: ''
                        })}
                      />
                      <label className="form-check-label">{t('district')}</label>
                    </div>
                  </div>
                </div>
              )}
              
              {formData.locationType === 'province' ? (
                <div className="mb-3">
                  <label htmlFor="provinceId" className="form-label">{t('province')} *</label>
                  <select
                    className="form-select"
                    id="provinceId"
                    value={formData.provinceId}
                    onChange={(e) => onFormChange({ provinceId: e.target.value })}
                    required
                  >
                    <option value="">{t('select.province')}</option>
                    {provinces.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="provinceIdForDistrict" className="form-label">{t('province')} *</label>
                    <select
                      className="form-select"
                      id="provinceIdForDistrict"
                      value={formData.provinceId}
                      onChange={(e) => onProvinceChange(e.target.value)}
                      required
                    >
                      <option value="">{t('select.province')}</option>
                      {provinces.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="districtId" className="form-label">{t('district')} *</label>
                    <select
                      className="form-select"
                      id="districtId"
                      value={formData.districtId}
                      onChange={(e) => onFormChange({ districtId: e.target.value })}
                      required
                      disabled={!formData.provinceId}
                    >
                      <option value="">{t('select.district')}</option>
                      {districts.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="mb-3">
                <label htmlFor="price" className="form-label">{t('price')} *</label>
                {/* Note: Using basic number input instead of InputNumber for simplicity */}
                {/* Original used Ant Design InputNumber with thousand separators */}
                <input
                  type="number"
                  className="form-control"
                  id="price"
                  value={formData.price}
                  onChange={(e) => onFormChange({ price: e.target.value })}
                  required
                  min="0"
                  step="1000"
                  placeholder="0"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="affectedDate" className="form-label">{t('affected.date')} *</label>
                <input
                  type="date"
                  className="form-control"
                  id="affectedDate"
                  value={formData.affectedDate}
                  onChange={(e) => onFormChange({ affectedDate: e.target.value })}
                  required
                />
              </div>

              <div className="mb-3">
                <label htmlFor="reason" className="form-label">{t('reason.for.change')}</label>
                <input
                  type="text"
                  className="form-control"
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => onFormChange({ reason: e.target.value })}
                  maxLength={500}
                  placeholder={t('optional')}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="notes" className="form-label">{t('notes')}</label>
                <textarea
                  className="form-control"
                  id="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => onFormChange({ notes: e.target.value })}
                  maxLength={500}
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t('cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {editingPrice ? t('update') : t('add')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PriceFormModal;
